/**
 * Retry policy — docs/spec/06 §4.
 *
 * Set once on the QueryClient, never scattered per call. These are pure
 * functions so the policy can be tested directly against status codes rather
 * than inferred from a screen: this is the configuration the whole app's error
 * behaviour rests on, and "the queue showed an error" is a bad way to find out
 * it is wrong.
 */
import { isApiError, isNetworkError } from './errors'

/** Queries retry at most twice (i.e. up to 3 attempts). */
export const MAX_QUERY_RETRIES = 2

/** Mutations get one retry, and only for a request that never reached the server. */
export const MAX_MUTATION_RETRIES = 1

const RETRYABLE_STATUS = (status: number): boolean =>
  status === 429 || (status >= 500 && status <= 599)

/**
 * Idempotent reads: retry network failures, 5xx, and 429.
 *
 * Never retry other 4xx — they are deterministic and retrying only delays the
 * real message. The 401 path is deliberately excluded here: it is handled by
 * the single-flight refresh-and-retry in the client interceptor (06 R1), and
 * retrying it here as well would multiply refreshes.
 *
 * The transient 503 seen live on `GET /jobs` is the reason this exists — it
 * must be absorbed silently, never rendered (04 §3).
 */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  if (failureCount >= MAX_QUERY_RETRIES) return false
  if (isNetworkError(error)) return true
  if (isApiError(error)) return RETRYABLE_STATUS(error.status)
  // An unrecognised throw is a bug, not a transient fault — surface it.
  return false
}

/**
 * Mutations do NOT auto-retry, with one exception: a pure network error, where
 * the request provably never reached the server, so a retry cannot duplicate a
 * submit. Any response — including a 5xx — means the server saw it, and a blind
 * retry risks the double-submit that 06 R3 exists to prevent.
 */
export function shouldRetryMutation(failureCount: number, error: unknown): boolean {
  if (failureCount >= MAX_MUTATION_RETRIES) return false
  return isNetworkError(error)
}

/**
 * Exponential backoff with a ceiling, honouring `Retry-After` when the api
 * sends one (06 §4). `attempt` is zero-based.
 */
export function retryDelayMs(attempt: number, error: unknown, maxMs = 30_000): number {
  if (isApiError(error) && error.retryAfterSeconds !== null) {
    return Math.min(error.retryAfterSeconds * 1000, maxMs)
  }
  return Math.min(1000 * 2 ** attempt, maxMs)
}
